'use client'

import { useState } from 'react'
import { SectionHeader } from '@/components/marketing/sections/section-header'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { integrationGroups } from '@/content/marketing/integrations-data'

const slug = (label: string) => label.toLowerCase().replace(/[^a-z0-9]+/g, '-')
const integrationTabs = integrationGroups.map(group => ({
  ...group,
  value: slug(group.label),
}))

export function IntegrationsSection() {
  const defaultTab = integrationTabs[0]?.value ?? ''
  const [activeTab, setActiveTab] = useState(defaultTab)
  const activeGroup =
    integrationTabs.find(group => group.value === activeTab) ??
    integrationTabs[0]

  if (!activeGroup) return null

  return (
    <section className="py-12 sm:py-16 md:py-24">
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center gap-8 px-5 sm:gap-12 sm:px-6">
        <div className="flex flex-col items-center text-center">
          <span className="inline-flex items-center gap-2 rounded-md border px-3 py-1 text-xs font-medium">
            Integrations
          </span>
          <div className="mt-4">
            <SectionHeader
              title="Connect with Your Own Data"
              description="Connect your marketing platforms, analytics tools, and creative engines. Permissions differ by integration — review the requested scope before connecting. Tools marked Requested can't be connected yet."
            />
          </div>
        </div>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex w-full flex-col items-center gap-8 sm:gap-10"
        >
          {/* No radius overrides: the shadcn tabs primitive is already fully
              round, which is the same geometry as the eyebrow chips and the
              hero CTAs. The old rounded-xl/rounded-lg here were fighting it. */}
          <TabsList className="bg-accent h-auto flex-wrap justify-center gap-1 p-1">
            {integrationTabs.map(group => (
              <TabsTrigger
                key={group.label}
                value={group.value}
                className="px-4 py-1.5 text-sm font-medium data-[state=active]:shadow-sm"
              >
                {group.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeGroup.value} className="w-full">
            {/* Keep inactive tab icons out of the DOM so they do not download on entry. */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeGroup.items.map(item => (
                <div
                  key={item.name}
                  className="bg-card rounded-2xl border p-5 sm:p-6"
                >
                  <div className="flex items-start justify-between gap-3">
                    <img
                      src={item.icon}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-9 w-9 object-contain sm:h-10 sm:w-10"
                    />
                    {item.status === 'requested' && (
                      <Badge variant="secondary" className="rounded-full">
                        Requested
                      </Badge>
                    )}
                  </div>
                  <div className="mt-4 flex flex-col gap-2">
                    <h3 className="text-base font-semibold sm:text-lg">
                      {item.name}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-6 sm:text-base">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  )
}
